import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, createApiClient } from '../App';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import {
  MagnifyingGlass,
  MapPin,
  Plus,
  ArrowSquareOut,
  Compass,
  SpinnerGap,
  Link as LinkIcon,
  BookOpen,
  CaretDown,
  CaretLeft,
  CaretRight,
  SortAscending,
  CalendarBlank,
  Clock,
  MapPinLine,
  Sparkle,
  Star,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const proxyImage = (url) => {
  if (!url) return null;
  // Don't proxy data URIs or already-proxied/local URLs
  if (url.startsWith('data:') || url.startsWith(BACKEND_URL)) return url;
  return `${BACKEND_URL}/api/proxy/image?url=${encodeURIComponent(url)}`;
};

const SOURCE_LABELS = {
  all: 'All Sources',
  dineplan: 'Dineplan',
  inside_guide: 'Inside Guide',
  timeout: 'Time Out',
  eat_out: 'Eat Out',
  google_places: 'Google',
};

const SORT_OPTIONS = [
  { value: 'recently_added', label: 'Recently Added' },
  { value: 'newest_articles', label: 'Newest Articles' },
  { value: 'newest_openings', label: 'Newest Openings' },
  { value: 'name_asc', label: 'Name A-Z' },
  { value: 'name_desc', label: 'Name Z-A' },
];

const Discovery = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const api = createApiClient(token);

  const [restaurants, setRestaurants] = useState([]);
  const [recentlyOpened, setRecentlyOpened] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSource, setActiveSource] = useState('all');
  const [activeSort, setActiveSort] = useState('recently_added');
  const [activeSuburb, setActiveSuburb] = useState('');
  const [availableSuburbs, setAvailableSuburbs] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [googleResults, setGoogleResults] = useState([]);
  const [googleSearching, setGoogleSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showSortMenu, setShowSortMenu] = useState(false);

  const PAGE_SIZE = 24;

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/discovery/recently-opened?limit=12`)
      .then(r => r.json())
      .then(data => setRecentlyOpened(data.items || []))
      .catch(() => {});
  }, []);

  const fetchDiscovery = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const params = new URLSearchParams({
        page: pageNum.toString(),
        page_size: PAGE_SIZE.toString(),
        sort: activeSort,
      });
      if (searchQuery) params.set('search', searchQuery);
      if (activeSource !== 'all') params.set('source', activeSource);
      if (activeSuburb) params.set('suburb', activeSuburb);

      const response = await fetch(`${BACKEND_URL}/api/discovery/restaurants?${params}`);
      const data = await response.json();

      if (append) {
        setRestaurants(prev => [...prev, ...(data.items || [])]);
      } else {
        setRestaurants(data.items || []);
      }
      setTotal(data.total || 0);
      setPage(pageNum);
      if (data.available_suburbs) {
        setAvailableSuburbs(data.available_suburbs);
      }
    } catch (error) {
      toast.error('Failed to load restaurants');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchQuery, activeSource, activeSort, activeSuburb]);

  useEffect(() => {
    fetchDiscovery(1, false);
  }, [activeSource, activeSort, activeSuburb]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDiscovery(1, false);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleLoadMore = () => {
    fetchDiscovery(page + 1, true);
  };

  const handleSaveToList = async (restaurant) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to save restaurants');
      navigate('/login');
      return;
    }
    setSavingId(restaurant.id || restaurant.place_id);
    try {
      if (restaurant.source_name === 'google_places' && restaurant.place_id) {
        await api.post('/restaurants', {
          name: restaurant.name,
          city: 'Cape Town',
          suburb: restaurant.suburb,
          cuisine: restaurant.cuisine || [],
          website_url: restaurant.website_url,
          hero_image_url: restaurant.image_url,
          description: restaurant.description,
          price_range: restaurant.price_range,
          address: restaurant.address,
          state: 'want_to_try',
          google_rating: restaurant.rating,
          google_rating_count: restaurant.rating_count,
          lat: restaurant.lat,
          lng: restaurant.lng,
        });
        toast.success(`Added ${restaurant.name} to your list!`);
        setGoogleResults(prev => prev.filter(r => r.place_id !== restaurant.place_id));
      } else {
        const response = await api.post(`/discovery/restaurants/${restaurant.id}/save`);
        if (response.data.exists) {
          toast.info('Restaurant already in your list');
        } else {
          toast.success(`Added ${restaurant.name} to your list!`);
        }
      }
    } catch {
      toast.error('Failed to save restaurant');
    } finally {
      setSavingId(null);
    }
  };

  const handleGoogleSearch = async () => {
    if (!searchQuery.trim()) return;
    if (!isAuthenticated) {
      toast.error('Please sign in to search Google');
      return;
    }
    setGoogleSearching(true);
    try {
      const res = await api.get(`/discovery/google-search?q=${encodeURIComponent(searchQuery.trim())}`);
      setGoogleResults(res.data.items || []);
      if (res.data.items?.length === 0) {
        toast.info('No Google results found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google search failed');
    } finally {
      setGoogleSearching(false);
    }
  };

  const hasMore = restaurants.length < total;
  const activeSortLabel = SORT_OPTIONS.find(o => o.value === activeSort)?.label || 'Sort';
  const showRecentlyOpened = recentlyOpened.length > 0 && !searchQuery && activeSource === 'all' && !activeSuburb;

  const heroImage = 'https://images.unsplash.com/photo-1644070648746-85150d916ab7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxjYXBlJTIwdG93biUyMHJlc3RhdXJhbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzUzMzg3NDR8MA&ixlib=rb-4.1.0&q=85';

  return (
    <div className="pb-8" data-testid="discovery-page">
      <SEO
        title="Discover Cape Town Restaurants"
        description="Browse hundreds of Cape Town restaurants. Find your next dining spot with ratings, reviews, cuisine types, and Google Places data."
        path="/discovery"
      />
      {/* Hero */}
      <div className="discovery-hero relative" style={{ backgroundImage: `url(${heroImage})` }} data-testid="discovery-hero">
        <div className="hero-overlay" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-4">
              Discover Cape Town
            </h1>
            <p className="text-lg sm:text-xl text-white/80 max-w-xl mx-auto">
              Find your next dining adventure from the best restaurants in the Mother City
            </p>
          </div>
        </div>
      </div>

      <div className="container-app py-8">
        {/* Search */}
        <div className="max-w-xl mx-auto mb-8 -mt-8 relative z-10">
          <div className="search-input-wrapper relative">
            <MagnifyingGlass className="search-icon" weight="duotone" />
            <Input
              type="text"
              placeholder="Search restaurants, suburbs, addresses..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setGoogleResults([]); }}
              className="pl-10 pr-32 py-6 text-lg rounded-full shadow-lg"
              data-testid="discovery-search"
              onKeyDown={(e) => { if (e.key === 'Enter' && searchQuery.trim()) handleGoogleSearch(); }}
            />
            {searchQuery.trim() && (
              <button
                onClick={handleGoogleSearch}
                disabled={googleSearching}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-4 py-2 bg-[#1A1918] text-white text-sm font-medium rounded-full hover:bg-[#2C2826] transition-colors disabled:opacity-50"
                data-testid="google-search-btn"
              >
                {googleSearching ? (
                  <SpinnerGap className="w-4 h-4 animate-spin" />
                ) : (
                  <MagnifyingGlass weight="bold" className="w-4 h-4" />
                )}
                Google
              </button>
            )}
          </div>
        </div>

        {/* Recently Opened Carousel */}
        {showRecentlyOpened && (
          <RecentlyOpenedSection
            items={recentlyOpened}
            onSave={handleSaveToList}
            savingId={savingId}
          />
        )}

        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex flex-wrap gap-2" data-testid="source-filters">
            {Object.entries(SOURCE_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveSource(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeSource === key
                    ? 'bg-[#4B5945] text-white shadow-md'
                    : 'bg-[#F5F1EC] text-[#5A5752] hover:bg-[#EAE8E3]'
                }`}
                data-testid={`source-filter-${key}`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex-1" />

          {availableSuburbs.length > 0 && (
            <select
              value={activeSuburb}
              onChange={(e) => setActiveSuburb(e.target.value)}
              className="px-3 py-2 rounded-full text-sm bg-[#F5F1EC] text-[#5A5752] border-0 cursor-pointer hover:bg-[#EAE8E3] transition-all appearance-none pr-8"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235A5752' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}
              data-testid="suburb-filter"
            >
              <option value="">All Suburbs</option>
              {availableSuburbs.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}

          <div className="relative">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-[#F5F1EC] text-[#5A5752] hover:bg-[#EAE8E3] transition-all"
              data-testid="sort-button"
            >
              <SortAscending weight="bold" className="w-4 h-4" />
              {activeSortLabel}
              <CaretDown weight="bold" className="w-3 h-3" />
            </button>
            {showSortMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowSortMenu(false)} />
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-[#EAE8E3] overflow-hidden z-50 min-w-[200px]" data-testid="sort-menu">
                  {SORT_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => { setActiveSort(option.value); setShowSortMenu(false); }}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                        activeSort === option.value
                          ? 'bg-[#4B5945] text-white'
                          : 'text-[#5A5752] hover:bg-[#F5F1EC]'
                      }`}
                      data-testid={`sort-option-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Results count */}
        {!loading && (
          <p className="text-[#7A7873] mb-6" data-testid="discovery-count">
            {total} restaurant{total !== 1 ? 's' : ''} found
            {searchQuery && <span className="text-[#A94438]"> for "{searchQuery}"</span>}
            {activeSuburb && <span className="text-[#4B5945]"> in {activeSuburb}</span>}
          </p>
        )}

        {/* Results grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="card-elevated animate-pulse">
                <div className="h-48 bg-[#E8DCD0] rounded-t-xl" />
                <div className="p-6 space-y-3">
                  <div className="h-6 bg-[#E8DCD0] rounded w-3/4" />
                  <div className="h-4 bg-[#E8DCD0] rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : restaurants.length === 0 ? (
          <div className="empty-state">
            <Compass weight="duotone" className="empty-state-icon" />
            <h3 className="empty-state-title">{searchQuery ? 'No restaurants found' : 'No restaurants to discover'}</h3>
            <p className="empty-state-text">{searchQuery ? 'Try a different search term' : 'Run the scrapers from the Admin page to populate this feed'}</p>
          </div>
        ) : (
          <>
            {/* Google Results Section */}
            {googleResults.length > 0 && (
              <div className="mb-10" data-testid="google-results-section">
                <div className="flex items-center gap-2 mb-4">
                  <MagnifyingGlass weight="bold" className="w-5 h-5 text-[#4285F4]" />
                  <h2 className="font-heading font-semibold text-[#1A1918]">Google Results</h2>
                  <span className="text-sm text-[#7A7873]">({googleResults.length})</span>
                </div>
                <motion.div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  initial="hidden" animate="visible"
                  variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.05 } } }}
                >
                  {googleResults.map((restaurant) => (
                    <DiscoveryCard
                      key={restaurant.place_id}
                      restaurant={restaurant}
                      onSave={() => handleSaveToList(restaurant)}
                      saving={savingId === restaurant.place_id}
                    />
                  ))}
                </motion.div>

                {restaurants.length > 0 && (
                  <div className="flex items-center gap-3 mt-8 mb-2">
                    <div className="flex-1 h-px bg-[#EAE8E3]" />
                    <span className="text-xs font-medium text-[#9A9590] uppercase tracking-wider">Scraped Results</span>
                    <div className="flex-1 h-px bg-[#EAE8E3]" />
                  </div>
                )}
              </div>
            )}

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              initial="hidden" animate="visible"
              variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.05 } } }}
            >
              {restaurants.map((restaurant) => (
                <DiscoveryCard key={restaurant.id} restaurant={restaurant} onSave={() => handleSaveToList(restaurant)} saving={savingId === restaurant.id} />
              ))}
            </motion.div>

            {hasMore && (
              <div className="flex justify-center mt-10">
                <Button onClick={handleLoadMore} disabled={loadingMore} variant="outline" className="px-8 py-3 rounded-full border-[#D4CFC8] text-[#5A5752] hover:bg-[#F5F1EC]" data-testid="load-more-btn">
                  {loadingMore ? <SpinnerGap className="w-5 h-5 animate-spin mr-2" /> : <CaretDown weight="bold" className="w-5 h-5 mr-2" />}
                  Load More ({restaurants.length} of {total})
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};


// ============= Recently Opened Carousel =============

const RecentlyOpenedSection = ({ items, onSave, savingId }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 340, behavior: 'smooth' });
  };

  const defaultImage = 'https://images.pexels.com/photos/1327393/pexels-photo-1327393.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

  return (
    <div className="mb-10" data-testid="recently-opened-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkle weight="fill" className="w-5 h-5 text-[#A94438]" />
          <h2 className="text-lg font-heading font-semibold text-[#1A1918]">Recently Opened</h2>
          <span className="text-sm text-[#7A7873]">New to the Cape Town scene</span>
        </div>
        <div className="flex gap-1">
          <button onClick={() => scroll(-1)} disabled={!canScrollLeft} className="w-8 h-8 rounded-full bg-[#F5F1EC] flex items-center justify-center disabled:opacity-30 hover:bg-[#EAE8E3] transition-colors" data-testid="scroll-left-btn">
            <CaretLeft weight="bold" className="w-4 h-4 text-[#5A5752]" />
          </button>
          <button onClick={() => scroll(1)} disabled={!canScrollRight} className="w-8 h-8 rounded-full bg-[#F5F1EC] flex items-center justify-center disabled:opacity-30 hover:bg-[#EAE8E3] transition-colors" data-testid="scroll-right-btn">
            <CaretRight weight="bold" className="w-4 h-4 text-[#5A5752]" />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {items.map((r) => (
          <div
            key={r.id}
            className="flex-shrink-0 w-[300px] snap-start card-elevated overflow-hidden"
            data-testid={`recently-opened-card-${r.id}`}
          >
            <div className="h-36 relative overflow-hidden">
              <img
                src={proxyImage(r.image_url) || defaultImage}
                alt={r.name}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.target.src = defaultImage; }}
              />
              <div className="absolute top-2 left-2">
                <Badge className="bg-[#A94438] text-white text-xs">
                  Opened {formatOpenedDate(r.opened_date)}
                </Badge>
              </div>
            </div>
            <div className="p-4">
              <h4 className="font-heading font-semibold text-[#1A1918] text-base mb-1 line-clamp-1">{r.name}</h4>
              {r.suburb && (
                <div className="flex items-center gap-1 text-[#7A7873] text-xs mb-2">
                  <MapPin weight="duotone" className="w-3.5 h-3.5" />
                  <span>{r.suburb}</span>
                </div>
              )}
              {(r.booking_url || r.source_url) && (
                <a href={r.booking_url || r.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-[#A94438] hover:underline mb-3" data-testid={`recently-opened-link-${r.id}`}>
                  <ArrowSquareOut weight="bold" className="w-3 h-3" />
                  <span>{r.booking_url ? 'Book / View' : `View on ${SOURCE_LABELS[r.source_name] || r.source_name}`}</span>
                </a>
              )}
              <Button
                onClick={() => onSave(r)}
                disabled={savingId === r.id}
                size="sm"
                className="w-full btn-primary text-sm"
                data-testid={`recently-opened-save-${r.id}`}
              >
                {savingId === r.id ? <SpinnerGap className="w-4 h-4 animate-spin" /> : <><Plus weight="bold" className="w-4 h-4 mr-1" /> Add to List</>}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ============= Helpers =============

function formatOpenedDate(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

function formatArticleDate(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}


// ============= Discovery Card =============

const DiscoveryCard = ({ restaurant, onSave, saving }) => {
  const defaultImage = 'https://images.pexels.com/photos/1327393/pexels-photo-1327393.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
  const openedDisplay = formatOpenedDate(restaurant.opened_date);
  const articleDateDisplay = formatArticleDate(restaurant.article_published_at);

  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
      className="card-elevated overflow-hidden"
      data-testid={`discovery-card-${restaurant.id}`}
    >
      <div className="h-48 relative overflow-hidden">
        <img
          src={proxyImage(restaurant.image_url) || defaultImage}
          alt={restaurant.name}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          loading="lazy"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          <Badge variant="secondary" className="bg-white/90 text-[#1A1918] text-xs">
            {SOURCE_LABELS[restaurant.source_name] || restaurant.source_name}
          </Badge>
          {openedDisplay && (
            <Badge variant="secondary" className="bg-[#4B5945]/90 text-white text-xs">
              Opened {openedDisplay}
            </Badge>
          )}
        </div>
        {restaurant.booking_url && (
          <div className="absolute top-3 right-3">
            <div className="w-8 h-8 bg-[#4B5945] rounded-full flex items-center justify-center" title="Has booking link">
              <LinkIcon weight="bold" className="w-4 h-4 text-white" />
            </div>
          </div>
        )}
      </div>

      <div className="p-5">
        <h3 className="font-heading text-xl font-semibold text-[#1A1918] mb-2 line-clamp-1">{restaurant.name}</h3>

        <div className="space-y-1 mb-3">
          {restaurant.suburb && (
            <div className="flex items-center gap-1.5 text-[#7A7873] text-sm">
              <MapPin weight="duotone" className="w-4 h-4 flex-shrink-0" />
              <span>{restaurant.suburb}</span>
            </div>
          )}
          {restaurant.address && !restaurant.suburb && (
            <div className="flex items-center gap-1.5 text-[#7A7873] text-sm">
              <MapPinLine weight="duotone" className="w-4 h-4 flex-shrink-0" />
              <span className="line-clamp-1">{restaurant.address}</span>
            </div>
          )}
        </div>

        {/* Cuisine / type tags + price + rating */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {(restaurant.cuisine || []).slice(0, 3).map((c, i) => (
            <span key={i} className="text-[10px] bg-[#F5E6C8]/50 text-[#8B6914] px-2 py-0.5 rounded-full font-medium">{c}</span>
          ))}
          {restaurant.price_range && (
            <span className="text-[10px] bg-[#E8DCD0] text-[#5A4E3C] px-2 py-0.5 rounded-full font-medium">{restaurant.price_range}</span>
          )}
          {restaurant.rating && (
            <span className="flex items-center gap-0.5 text-[10px] text-[#C4A35A] font-medium">
              <Star weight="fill" className="w-3 h-3" />{restaurant.rating}
              {restaurant.rating_count && <span className="text-[#9A9590]">({restaurant.rating_count})</span>}
            </span>
          )}
        </div>

        {restaurant.description && (
          <p className="text-sm text-[#7A7873] line-clamp-2 mb-3">{restaurant.description}</p>
        )}

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#9A9590] mb-3">
          {restaurant.opening_times && (
            <div className="flex items-center gap-1" title={restaurant.opening_times}>
              <Clock weight="duotone" className="w-3.5 h-3.5" />
              <span className="line-clamp-1 max-w-[180px]">{restaurant.opening_times}</span>
            </div>
          )}
          {articleDateDisplay && (
            <div className="flex items-center gap-1">
              <CalendarBlank weight="duotone" className="w-3.5 h-3.5" />
              <span>Article: {articleDateDisplay}</span>
            </div>
          )}
        </div>

        {restaurant.article_url && restaurant.article_title && (
          <a href={restaurant.article_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#A94438] hover:underline mb-4" data-testid={`read-article-${restaurant.id}`}>
            <BookOpen weight="duotone" className="w-4 h-4 flex-shrink-0" />
            <span className="line-clamp-1">Read: {restaurant.article_title}</span>
            <ArrowSquareOut weight="bold" className="w-3 h-3 flex-shrink-0" />
          </a>
        )}

        {!restaurant.article_url && restaurant.source_url && (
          <a href={restaurant.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#A94438] hover:underline mb-4" data-testid={`view-source-${restaurant.id}`}>
            <ArrowSquareOut weight="duotone" className="w-4 h-4 flex-shrink-0" />
            <span className="line-clamp-1">View on {SOURCE_LABELS[restaurant.source_name] || restaurant.source_name}</span>
          </a>
        )}

        <div className="flex gap-2">
          <Button onClick={onSave} disabled={saving} className="flex-1 btn-primary" data-testid={`save-btn-${restaurant.id}`}>
            {saving ? <SpinnerGap className="w-5 h-5 animate-spin" /> : <><Plus weight="bold" className="w-5 h-5 mr-2" /> Add to List</>}
          </Button>
          {(restaurant.booking_url || restaurant.source_url) && (
            <a href={restaurant.booking_url || restaurant.source_url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-12 h-12 rounded-full border border-[#EAE8E3] hover:bg-[#F9F8F5] transition-colors" title={restaurant.booking_url ? "Book now" : "View listing"} data-testid={`book-btn-${restaurant.id}`}>
              <ArrowSquareOut weight="bold" className="w-5 h-5 text-[#7A7873]" />
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Discovery;
